import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const RealtimeContext = createContext(null);

export const RealtimeProvider = ({ children }) => {
  const [isConnected] = useState(false); // Disabled real-time for now
  const [lastUpdate, setLastUpdate] = useState(null);
  const listenersRef = useRef({
    transaction: [],
    subscription: [],
    budgetAlert: [],
    settings: [],
    split: [],
    notification: [],
  });

  // Register a listener for a specific event type
  const addListener = useCallback((type, callback) => {
    if (listenersRef.current[type]) {
      listenersRef.current[type].push(callback);
    }
    return () => {
      if (listenersRef.current[type]) {
        listenersRef.current[type] = listenersRef.current[type].filter(cb => cb !== callback);
      }
    };
  }, []);

  // Notify all listeners of a specific type
  const notifyListeners = useCallback((type, payload) => {
    if (listenersRef.current[type]) {
      listenersRef.current[type].forEach(callback => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`Error in ${type} listener:`, error);
        }
      });
    }
  }, []);

  // Check budget limits and trigger alerts
  const checkBudgetAlerts = useCallback((categorySpending, budgetGoals, formatMoney) => {
    if (!categorySpending || !budgetGoals) return [];
    
    const alerts = [];
    
    Object.entries(categorySpending).forEach(([category, spent]) => {
      const budget = budgetGoals[category] || 0;
      if (budget > 0) {
        const percentage = (spent / budget) * 100;
        
        if (percentage >= 100 && percentage < 110) {
          const alert = {
            type: 'exceeded',
            category,
            spent,
            budget,
            percentage,
            message: `${category} budget exceeded! ${formatMoney ? formatMoney(spent) : `$${spent.toFixed(2)}`} spent of ${formatMoney ? formatMoney(budget) : `$${budget.toFixed(2)}`} budget`
          };
          alerts.push(alert);
          
          toast.warning(`Budget exceeded: ${category}`, {
            description: alert.message,
            duration: 5000,
          });
        } else if (percentage >= 80 && percentage < 100) {
          const alert = {
            type: 'warning',
            category,
            spent,
            budget,
            percentage,
            message: `${category} at ${percentage.toFixed(0)}% of budget`
          };
          alerts.push(alert);
        }
      }
    });

    return alerts;
  }, []);

  // Trigger update manually (for components that need to signal changes)
  const triggerUpdate = useCallback((type, payload) => {
    setLastUpdate({ type, timestamp: Date.now(), payload });
    notifyListeners(type, payload);
  }, [notifyListeners]);

  const value = {
    isConnected,
    lastUpdate,
    addListener,
    checkBudgetAlerts,
    triggerUpdate,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

export default RealtimeContext;
